
import Vue from "vue";
import { format, formatDistanceToNow } from "date-fns";
import {
  raterAccountsMapActions,
  raterAccountsMapGetters
} from "@/store/modules/raterAccounts";
import { IRaterAccountResetResult } from "@/store/modules/raterAccounts/types";

export default Vue.extend({
  name: "RaterAccountPool",
  data() {
    return {
      errorMessage: "",
      successMessage: "",
      showForceOption: false,
      forceConfirmed: false
    };
  },
  created() {
    this.fetchStatus();
  },
  computed: {
    ...raterAccountsMapGetters([
      "raterAccountStatus",
      "raterAccountPool",
      "raterAccountQueue",
      "raterAccountResetResult",
      "isPoolExhausted",
      "jobLooksWedged",
      "isLoading"
    ]),
    status(): any {
      return this.raterAccountStatus;
    },
    pool(): any {
      return this.raterAccountPool;
    },
    queue(): any {
      return this.raterAccountQueue;
    },
    topActionButtonPrimary(): Record<string, any> {
      return {
        text: "Refresh",
        key: "refresh",
        loading: this.isLoading
      };
    },
    hasAccountsStillRating(): boolean {
      return Boolean(this.pool && this.pool.inUseWithinCooldown > 0);
    },
    verdictTitle(): string {
      if (this.isPoolExhausted) {
        return "Pool is empty — rating is failing";
      }
      if (this.jobLooksWedged) {
        return "Pool has accounts, but the reset job is stuck";
      }
      return "Pool is healthy";
    },
    verdictDetail(): string {
      if (this.isPoolExhausted) {
        return this.pool.reclaimable > 0
          ? `No accounts are available. ${this.pool.reclaimable} look stuck and can be released safely.`
          : "No accounts are available, and every one of them is still within its rating window. Wait a moment and refresh before forcing a release.";
      }
      if (this.jobLooksWedged) {
        return `${this.pool.available} accounts are still available, but the job that returns them has stalled. Release stuck accounts before the pool drains.`;
      }
      return `${this.pool.available} of ${this.pool.totalAccounts} accounts are available for rating.`;
    },
    verdictBannerClass(): string {
      if (this.isPoolExhausted) {
        return "bg-red-50 border-red-500 text-red-900";
      }
      if (this.jobLooksWedged) {
        return "bg-yellow-50 border-yellow-500 text-yellow-900";
      }
      return "bg-green-50 border-green-500 text-green-900";
    },
    availableTileClass(): string {
      return this.isPoolExhausted
        ? "bg-red-100 text-red-900"
        : "bg-green-50 text-green-900";
    },
    reclaimableTileClass(): string {
      return this.pool && this.pool.reclaimable > 0
        ? "bg-yellow-50 text-yellow-900"
        : "bg-gray-50 text-gray-600";
    },
    relativeLastRunAt(): string {
      return this.queue ? this.relative(this.queue.lastRunAt) : "Unknown";
    },
    forceDescription(): string {
      if (!this.hasAccountsStillRating) {
        return "Nothing is currently mid-rating, so forcing does exactly what the safe release does. It carries no extra risk right now.";
      }
      return `${this.pool.inUseWithinCooldown} accounts are still rating. Forcing takes those back too, which can break the quotes using them.`;
    },
    forceRiskStatement(): string {
      if (!this.hasAccountsStillRating) {
        return "No accounts are currently mid-rating, so this is equivalent to the safe release and costs nothing.";
      }
      return `This releases all ${this.pool.inUse} checked-out accounts, including ${this.pool.inUseWithinCooldown} that may still be mid-rating.`;
    },
    resetSummary(): string {
      const result = this
        .raterAccountResetResult as IRaterAccountResetResult | null;
      if (!result) return "";
      const { released, skippedRecentlyUsed } = result.raterAccounts;
      let summary = `Released ${released} ${this.pluralAccounts(
        released
      )}, skipped ${skippedRecentlyUsed} still in use.`;
      if (result.queue && result.queue.unlocked > 0) {
        summary += " Cleared stuck reset job.";
      }
      return summary;
    },
    queueResetWarning(): string {
      const result = this
        .raterAccountResetResult as IRaterAccountResetResult | null;
      if (result && result.queue && result.queue.error) {
        return `The job queue could not be reached (${result.queue.error}), so the stuck job was not cleared. The accounts above were still released.`;
      }
      return "";
    }
  },
  methods: {
    ...raterAccountsMapActions(["getRaterAccountStatus", "resetRaterAccounts"]),
    pluralAccounts(count: number): string {
      return count === 1 ? "account" : "accounts";
    },
    relative(value: string | null): string {
      if (!value) return "Never";
      return `${formatDistanceToNow(new Date(value))} ago`;
    },
    absolute(value: string | null): string {
      if (!value) return "";
      return format(new Date(value), "M/d/yyyy h:mm:ss a");
    },
    async fetchStatus(): Promise<void> {
      try {
        await this.getRaterAccountStatus();
      } catch (error) {
        this.errorMessage = (error as Error).message;
        this.$bugSnagClient.notify(error);
      }
    },
    openForceConfirmation(): void {
      this.forceConfirmed = false;
      this.$modal.show("forceResetModal");
    },
    async confirmForceReset(): Promise<void> {
      if (!this.forceConfirmed) return;
      this.$modal.hide("forceResetModal");
      await this.runReset(true);
    },
    async runReset(force: boolean): Promise<void> {
      this.errorMessage = "";
      try {
        await this.resetRaterAccounts(force);
        if (this.resetSummary) {
          this.$appNotifySuccess(this.resetSummary);
        }
        this.showForceOption = false;
        await this.fetchStatus();
      } catch (error) {
        this.errorMessage = (error as Error).message;
        this.$bugSnagClient.notify(error);
      }
    },
    onToolbarItemClick(action: string): void {
      switch (action) {
        case "refresh":
          this.fetchStatus();
          break;
      }
    }
  }
});
